import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import {
	Box,
	Button,
	Typography,
	TextField,
	IconButton,
	Alert,
	Switch,
	CircularProgress,
	Collapse,
	SwipeableDrawer,
	useMediaQuery,
	useTheme
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { Icon } from '@iconify/react'
import { supabase } from '../../utils/supabase'
import {
	fetchMerchantPhotos,
	uploadMerchantGalleryPhoto,
	uploadMerchantGalleryPhotoBatch,
	deleteMerchantPhoto,
	updatePhotoOrders
} from '../../utils/merchantPhotoUtils'
import GalleryPhotoCard from './GalleryPhotoCard'
import BusinessInfoSection from './BusinessInfoSection'
import MainButton from '../ui/MainButton'

// Card wrapper component
function Card({ children, sx = {} }) {
	return (
		<Box
			sx={{
				bgcolor: '#FFFFFF',
				borderRadius: '12px',
				border: '1px solid rgba(0, 0, 0, 0.2)',
				p: '16px 24px',
				...sx
			}}
		>
			{children}
		</Box>
	)
}

// Collapsible section component
function CollapsibleSection({ title, defaultExpanded = false, children, isMobile }) {
	const [expanded, setExpanded] = useState(defaultExpanded)

	return (
		<Box
			sx={{
				bgcolor: '#FFFFFF',
				borderRadius: '12px',
				border: '1px solid rgba(0, 0, 0, 0.2)'
			}}
		>
			<Box
				onClick={() => setExpanded(!expanded)}
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					p: '20px 24px',
					cursor: 'pointer',
					userSelect: 'none',
					borderRadius: expanded ? '12px 12px 0 0' : '12px',
					'&:hover': { bgcolor: '#FAFAFA' }
				}}
			>
				<Typography
					sx={{
						fontFamily: 'Inter, sans-serif',
						fontWeight: 400,
						fontSize: isMobile ? '18px' : '22px',
						lineHeight: '1.5em',
						color: '#000000',
						flex: 1
					}}
				>
					{title}
				</Typography>
				{expanded ? (
					<ExpandLessIcon sx={{ color: '#4A5568' }} />
				) : (
					<ExpandMoreIcon sx={{ color: '#4A5568' }} />
				)}
			</Box>
			<Collapse in={expanded}>
				<Box sx={{ px: '24px', pb: '20px' }}>
					{children}
				</Box>
			</Collapse>
		</Box>
	)
}

// File drop zone component
function FileDropZone({ onFileSelect, accept = 'image/jpeg,image/jpg,image/png', label = 'Drag and Drop or Click to Upload', multiple = false }) {
	const fileInputRef = useRef(null)
	const [isDragging, setIsDragging] = useState(false)

	const handleDragOver = (e) => {
		e.preventDefault()
		setIsDragging(true)
	}

	const handleDragLeave = (e) => {
		e.preventDefault()
		setIsDragging(false)
	}

	const handleDrop = (e) => {
		e.preventDefault()
		setIsDragging(false)
		const files = e.dataTransfer.files
		if (files.length > 0) {
			if (multiple) {
				onFileSelect(files)
			} else {
				onFileSelect(files[0])
			}
		}
	}

	const handleClick = () => {
		fileInputRef.current?.click()
	}

	const handleFileChange = (e) => {
		const files = e.target.files
		if (files && files.length > 0) {
			if (multiple) {
				onFileSelect(files)
			} else {
				onFileSelect(files[0])
			}
			e.target.value = ''
		}
	}

	return (
		<Box
			onClick={handleClick}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
			sx={{
				width: '100%',
				height: '160px',
				bgcolor: '#F7F6F3',
				border: `1px dashed ${isDragging ? '#9F7AEA' : '#CBD5E0'}`,
				borderRadius: '12px',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				gap: '8px',
				cursor: 'pointer',
				transition: 'all 0.2s ease',
				'&:hover': {
					borderColor: '#9F7AEA',
					bgcolor: '#F3F0FF'
				}
			}}
		>
			<input
				ref={fileInputRef}
				type="file"
				accept={accept}
				multiple={multiple}
				onChange={handleFileChange}
				style={{ display: 'none' }}
			/>
			<CloudUploadIcon sx={{ fontSize: 28, color: '#A0AEC0' }} />
			<Typography
				sx={{
					fontFamily: 'Inter, sans-serif',
					fontWeight: 600,
					fontSize: '14px',
					lineHeight: '1.21em',
					color: '#2D3748'
				}}
			>
				{label}
			</Typography>
			<Typography
				sx={{
					fontFamily: 'Inter, sans-serif',
					fontWeight: 400,
					fontSize: '12px',
					lineHeight: '1.21em',
					color: '#4A5568'
				}}
			>
				JPEG, JPG, PNG
			</Typography>
		</Box>
	)
}

// Preview Panel - shared between desktop right panel and mobile drawer
function PreviewPanel({ previewMode, setPreviewMode, iframeKey, iframeRef, mobileContainerRef, mobileScale, getBookingUrl, refreshPreview, isMobile }) {
	const desktopScreenRef = useRef(null)
	const [desktopIframeScale, setDesktopIframeScale] = useState(0.4)

	const DESKTOP_IFRAME_WIDTH = 1280

	useEffect(() => {
		const screen = desktopScreenRef.current
		if (!screen || previewMode !== 'desktop') return

		const updateScale = () => {
			const sw = screen.clientWidth
			if (sw === 0) return
			setDesktopIframeScale(sw / DESKTOP_IFRAME_WIDTH)
		}

		const timeout = setTimeout(updateScale, 50)
		const observer = new ResizeObserver(updateScale)
		observer.observe(screen)
		return () => {
			clearTimeout(timeout)
			observer.disconnect()
		}
	}, [previewMode])

	return (
		<Box
			sx={{
				bgcolor: '#FFFFFF',
				borderRadius: '12px',
				p: '16px 24px',
				display: 'flex',
				flexDirection: 'column',
				flex: 1,
				overflow: 'hidden'
			}}
		>
			{/* Preview Header */}
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '18px', mb: 1 }}>
				<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D3748' }}>
					Live Preview
				</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', ml: 'auto' }}>
					<IconButton
						onClick={() => setPreviewMode('desktop')}
						size="small"
						sx={{
							bgcolor: previewMode === 'desktop' ? '#E9D8FD' : 'transparent',
							borderRadius: '8px',
							p: '6px',
							'&:hover': { bgcolor: previewMode === 'desktop' ? '#D6BCFA' : '#F7FAFC' }
						}}
					>
						<Icon icon="mi:computer" width={22} color={previewMode === 'desktop' ? '#553C9A' : '#A0AEC0'} />
					</IconButton>
					<IconButton
						onClick={() => setPreviewMode('mobile')}
						size="small"
						sx={{
							bgcolor: previewMode === 'mobile' ? '#E9D8FD' : 'transparent',
							borderRadius: '8px',
							p: '6px',
							'&:hover': { bgcolor: previewMode === 'mobile' ? '#D6BCFA' : '#F7FAFC' }
						}}
					>
						<Icon icon="fluent:phone-28-regular" width={22} color={previewMode === 'mobile' ? '#553C9A' : '#A0AEC0'} />
					</IconButton>
					<IconButton
						onClick={refreshPreview}
						size="small"
						sx={{
							borderRadius: '8px',
							p: '6px',
							'&:hover': { bgcolor: '#F7FAFC' }
						}}
					>
						<Icon icon="material-symbols:refresh-rounded" width={22} color="#464646" />
					</IconButton>
				</Box>
			</Box>

			<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '13px', color: '#A0AEC0', mb: '18px', mt: '14px' }}>
				Live preview will auto-refresh when you make changes. Swap between desktop and mobile views.
			</Typography>

			{/* Iframe Container */}
			<Box
				ref={mobileContainerRef}
				sx={{
					flex: 1,
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					overflow: 'hidden',
					bgcolor: '#FFFFFF',
					borderRadius: '8px',
				}}
			>
				{previewMode === 'mobile' ? (
					<Box
						sx={{
							width: '250px',
							height: '517px',
							borderRadius: '44px',
							position: 'relative',
							bgcolor: '#B2ABA2',
							boxShadow: 'inset 0px 0px 1px 1px rgba(0,0,0,0.25), inset 0px 0px 0.4px 0.4px rgba(0,0,0,0.2), 0 8px 32px rgba(0,0,0,0.15)',
							flexShrink: 0,
							transform: `scale(${mobileScale})`,
							transformOrigin: 'center center'
						}}
					>
						<Box sx={{
							position: 'absolute',
							top: '3.2px',
							left: '5px',
							width: '240px',
							height: '510px',
							borderRadius: '40px',
							bgcolor: '#1D1D1F',
							overflow: 'hidden'
						}}>
							<Box sx={{
								position: 'absolute',
								top: '10px',
								left: '50%',
								transform: 'translateX(-50%)',
								width: '71px',
								height: '20px',
								borderRadius: '26px',
								bgcolor: '#000',
								zIndex: 2
							}} />
							{/* Visible screen with rounded corners */}
							<Box sx={{
								position: 'absolute',
								top: '5px',
								left: '5px',
								width: '230px',
								height: '500px',
								borderRadius: '35px',
								overflow: 'hidden',
								bgcolor: '#fff'
							}}>
								<iframe
									key={iframeKey}
									ref={iframeRef}
									src={getBookingUrl()}
									title="Mobile Preview"
									style={{
										width: '375px',
										height: '812px',
										border: 'none',
										transform: 'scale(0.615)',
										transformOrigin: 'top left'
									}}
								/>
							</Box>
						</Box>
					</Box>
				) : (
					// Desktop monitor frame - fills container via aspect-ratio
					<Box
						sx={{
							width: '100%',
							height: '100%',
							display: 'flex',
							justifyContent: 'center',
							alignItems: isMobile ? 'center' : 'flex-start',
							p: '0px'
						}}
					>
						<Box
							sx={{
								width: '100%',
								maxHeight: '100%',
								aspectRatio: '518 / 342',
								bgcolor: '#C4C4C4',
								borderRadius: '13px',
								position: 'relative',
								overflow: 'hidden'
							}}
						>
							{/* Camera notch */}
							<Box sx={{
								position: 'absolute',
								top: '3px',
								left: '50%',
								transform: 'translateX(-50%)',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								zIndex: 3
							}}>
								<Box sx={{ width: '6px', height: '3px', borderRadius: '50%', bgcolor: '#989898' }} />
							</Box>

							{/* Screen area */}
							<Box sx={{
								position: 'absolute',
								top: '3%',
								left: '1.7%',
								right: '1.7%',
								bottom: '3%',
								borderRadius: '1px',
								overflow: 'hidden',
								bgcolor: '#fff',
								display: 'flex',
								flexDirection: 'column'
							}}>
								{/* Browser bar */}
								<Box sx={{
									height: '4.1%',
									minHeight: '14px',
									bgcolor: '#ECECEC',
									display: 'flex',
									alignItems: 'center',
									px: '8px',
									gap: '3px',
									flexShrink: 0,
									boxShadow: '0px 1.4px 4.2px rgba(0, 0, 0, 0.14)'
								}}>
									<Box sx={{ width: '4px', height: '4px', borderRadius: '50%', bgcolor: '#FF5F57' }} />
									<Box sx={{ width: '4px', height: '4px', borderRadius: '50%', bgcolor: '#FEBC2E' }} />
									<Box sx={{ width: '4px', height: '4px', borderRadius: '50%', bgcolor: '#28C840' }} />
									<Box sx={{
										flex: 1,
										height: '7px',
										bgcolor: '#FFFFFF',
										borderRadius: '3px',
										ml: '30px',
										mr: '30px',
										border: '0.5px solid #D4D4D4'
									}} />
								</Box>

								{/* Iframe content */}
								<Box ref={desktopScreenRef} sx={{ flex: 1, overflow: 'hidden' }}>
									<iframe
										key={iframeKey}
										ref={iframeRef}
										src={getBookingUrl()}
										title="Desktop Preview"
										style={{
											width: `${DESKTOP_IFRAME_WIDTH}px`,
											height: `${Math.round(1 / desktopIframeScale * 100)}%`,
											border: 'none',
											transform: `scale(${desktopIframeScale})`,
											transformOrigin: 'top left'
										}}
									/>
								</Box>
							</Box>
						</Box>
					</Box>
				)}
			</Box>
		</Box>
	)
}

export default function LandingPageSettings({ merchant, onSaved, hasUnsavedChangesRef }) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	// Mobile preview drawer state
	const [previewDrawerOpen, setPreviewDrawerOpen] = useState(false)

	// ============ Form state ============
	const [slug, setSlug] = useState(merchant?.slug || '')
	const [isPublished, setIsPublished] = useState(merchant?.is_landing_page_active ?? false)

	// Photo state
	const [galleryPhotos, setGalleryPhotos] = useState([])
	const [loadingPhotos, setLoadingPhotos] = useState(true)

	// Upload state
	const [uploadingGallery, setUploadingGallery] = useState(false)

	// UI state
	const [saving, setSaving] = useState(false)
	const [errorMessage, setErrorMessage] = useState('')
	const [successMessage, setSuccessMessage] = useState('')
	const [copied, setCopied] = useState(false)

	// Drag state for gallery reordering
	const [draggedIndex, setDraggedIndex] = useState(null)
	const [dragOverIndex, setDragOverIndex] = useState(null)
	const [dropPosition, setDropPosition] = useState(null)

	// Gallery container ref
	const galleryContainerRef = useRef(null)
	const touchStartDataRef = useRef(null)

	// Preview state
	const [previewMode, setPreviewMode] = useState('desktop') // 'desktop' | 'mobile'
	const [iframeKey, setIframeKey] = useState(0)
	const iframeRef = useRef(null)
	const mobileContainerRef = useRef(null)
	const [mobileScale, setMobileScale] = useState(1)

	// ============ Business Information State ============
	const [bizAddress, setBizAddress] = useState(merchant?.address || '')
	const [bizAddress2, setBizAddress2] = useState(merchant?.address_2 || '')
	const [bizCity, setBizCity] = useState(merchant?.city || '')
	const [bizState, setBizState] = useState(merchant?.state || '')
	const [bizZip, setBizZip] = useState(merchant?.zip || '')
	const [bizPhone, setBizPhone] = useState(merchant?.phone || '')
	const [bizEmail, setBizEmail] = useState(merchant?.email || '')
	const [bizDescription, setBizDescription] = useState(merchant?.description || '')
	const [bizWebsite, setBizWebsite] = useState(merchant?.website || '')
	const [bizSocialLinks, setBizSocialLinks] = useState({
		instagram: merchant?.social_links?.instagram || '',
		google: merchant?.social_links?.google || '',
		yelp: merchant?.social_links?.yelp || '',
		facebook: merchant?.social_links?.facebook || ''
	})
	// Hours inheritance
	const emptyHours = useMemo(() => ({ "0": [], "1": [], "2": [], "3": [], "4": [], "5": [], "6": [] }), [])
	const defaultDisplayHours = useMemo(() => ({
		"0": [], "1": [{ start: '09:00', end: '17:00' }], "2": [{ start: '09:00', end: '17:00' }],
		"3": [{ start: '09:00', end: '17:00' }], "4": [{ start: '09:00', end: '17:00' }],
		"5": [{ start: '09:00', end: '17:00' }], "6": []
	}), [])
	const [bizInheritedHours, setBizInheritedHours] = useState(merchant?.inherited_hours ?? true)
	const isDisplayHoursEmpty = (h) => !h || Object.values(h).every(v => !v || v.length === 0)
	// Custom display hours start from the merchant's REAL store hours, so the
	// editor (and anything saved from it) stays in sync with weekly_hours —
	// the hardcoded Mon–Fri 9–5 default is only a last resort when store
	// hours are empty too.
	const seedDisplayHours = useCallback(() => {
		if (!isDisplayHoursEmpty(merchant?.weekly_hours_display)) return merchant.weekly_hours_display
		if (!isDisplayHoursEmpty(merchant?.weekly_hours)) return merchant.weekly_hours
		return defaultDisplayHours
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [merchant, defaultDisplayHours])
	const [bizWeeklyHoursDisplay, setBizWeeklyHoursDisplay] = useState(seedDisplayHours)
	const bizWeeklyHoursInherited = merchant?.weekly_hours || emptyHours
	const bizClosedDates = merchant?.closed_recurring_dates || []

	const MAX_GALLERY_PHOTOS = 10

	// ============ Dirty tracking ============
	const hasUnsavedChanges = useMemo(() => {
		if (!merchant) return false
		const checks = [
			isPublished !== (merchant.is_landing_page_active ?? false),
			(slug || '') !== (merchant.slug || ''),
			(bizAddress || '') !== (merchant.address || ''),
			(bizAddress2 || '') !== (merchant.address_2 || ''),
			(bizCity || '') !== (merchant.city || ''),
			(bizState || '') !== (merchant.state || ''),
			(bizZip || '') !== (merchant.zip || ''),
			(bizPhone || '') !== (merchant.phone || ''),
			(bizEmail || '') !== (merchant.email || ''),
			(bizDescription || '') !== (merchant.description || ''),
			(bizWebsite || '') !== (merchant.website || ''),
			JSON.stringify(bizSocialLinks) !== JSON.stringify({
				instagram: merchant.social_links?.instagram || '',
				google: merchant.social_links?.google || '',
				yelp: merchant.social_links?.yelp || '',
				facebook: merchant.social_links?.facebook || ''
			}),
			bizInheritedHours !== (merchant.inherited_hours ?? true),
			JSON.stringify(bizWeeklyHoursDisplay) !== JSON.stringify(seedDisplayHours())
		]
		return checks.some(Boolean)
	}, [merchant, isPublished, slug, bizAddress, bizAddress2, bizCity, bizState, bizZip, bizPhone, bizEmail, bizDescription, bizWebsite, bizSocialLinks, bizInheritedHours, bizWeeklyHoursDisplay, seedDisplayHours])

	// Update parent ref for unsaved changes
	useEffect(() => {
		if (hasUnsavedChangesRef) {
			hasUnsavedChangesRef.current = hasUnsavedChanges
		}
	}, [hasUnsavedChanges, hasUnsavedChangesRef])

	// Browser beforeunload warning
	useEffect(() => {
		const handleBeforeUnload = (e) => {
			if (hasUnsavedChanges) {
				e.preventDefault()
				e.returnValue = ''
			}
		}
		window.addEventListener('beforeunload', handleBeforeUnload)
		return () => window.removeEventListener('beforeunload', handleBeforeUnload)
	}, [hasUnsavedChanges])

	// ============ Initialize from merchant ============
	useEffect(() => {
		if (!merchant) return
		setBizAddress(merchant.address || '')
		setBizAddress2(merchant.address_2 || '')
		setBizCity(merchant.city || '')
		setBizState(merchant.state || '')
		setBizZip(merchant.zip || '')
		setBizPhone(merchant.phone || '')
		setBizEmail(merchant.email || '')
		setBizDescription(merchant.description || '')
		setBizWebsite(merchant.website || '')
		setBizSocialLinks({
			instagram: merchant.social_links?.instagram || '',
			google: merchant.social_links?.google || '',
			yelp: merchant.social_links?.yelp || '',
			facebook: merchant.social_links?.facebook || ''
		})
		setBizInheritedHours(merchant.inherited_hours ?? true)
		setBizWeeklyHoursDisplay(seedDisplayHours())
	}, [merchant, emptyHours, seedDisplayHours])

	// Scale mobile preview to fit container
	useEffect(() => {
		const container = mobileContainerRef.current
		if (!container || previewMode !== 'mobile') return

		const PHONE_W = 250
		const PHONE_H = 517
		const PADDING = 24 // some breathing room

		const updateScale = () => {
			const cw = container.clientWidth - PADDING
			const ch = container.clientHeight - PADDING
			const scaleW = cw / PHONE_W
			const scaleH = ch / PHONE_H
			const scale = Math.min(scaleW, scaleH, 1) // never scale up beyond 1
			setMobileScale(scale)
		}

		updateScale()
		const observer = new ResizeObserver(updateScale)
		observer.observe(container)
		return () => observer.disconnect()
	}, [previewMode])

	// Load photos on mount
	useEffect(() => {
		if (merchant?.id) {
			loadPhotos()
		}
	}, [merchant?.id])

	// Initialize slug from merchant
	useEffect(() => {
		if (merchant?.slug) {
			setSlug(merchant.slug)
		}
	}, [merchant?.slug])

	// ============ Photo handlers ============
	const loadPhotos = async () => {
		setLoadingPhotos(true)
		try {
			const { gallery } = await fetchMerchantPhotos(merchant.id)
			setGalleryPhotos(gallery)
		} catch (error) {
			console.error('Error loading photos:', error)
		} finally {
			setLoadingPhotos(false)
		}
	}

	const refreshPreview = useCallback(() => {
		setIframeKey(prev => prev + 1)
	}, [])

	const getBookingUrl = () => {
		const baseUrl = window.location.origin
		return `${baseUrl}/store/${slug || merchant?.id}`
	}

	const handleCopyLink = async () => {
		try {
			await navigator.clipboard.writeText(getBookingUrl())
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		} catch (error) {
			console.error('Failed to copy:', error)
		}
	}

	const handlePreviewPage = () => {
		window.open(getBookingUrl(), '_blank')
	}

	const handleGalleryFileSelect = async (files) => {
		// Support both single File and FileList/Array
		const fileArray = files instanceof FileList ? Array.from(files) : (Array.isArray(files) ? files : [files])
		const remaining = MAX_GALLERY_PHOTOS - galleryPhotos.length
		if (remaining <= 0) {
			setErrorMessage(`Maximum ${MAX_GALLERY_PHOTOS} photos allowed`)
			return
		}
		const filesToUpload = fileArray.slice(0, remaining)
		if (filesToUpload.length < fileArray.length) {
			setErrorMessage(`Only ${remaining} more photo${remaining === 1 ? '' : 's'} can be added. ${fileArray.length - remaining} file${fileArray.length - remaining === 1 ? ' was' : 's were'} skipped.`)
		}

		setUploadingGallery(true)
		try {
			if (filesToUpload.length === 1) {
				const result = await uploadMerchantGalleryPhoto(merchant.id, filesToUpload[0])
				if (result) {
					setGalleryPhotos(prev => [...prev, result.photo])
					setSuccessMessage('Photo uploaded successfully')
					setTimeout(() => setSuccessMessage(''), 3000)
					refreshPreview()
				} else {
					setErrorMessage('Failed to upload photo')
				}
			} else {
				const result = await uploadMerchantGalleryPhotoBatch(merchant.id, filesToUpload)
				if (result && result.photos.length > 0) {
					setGalleryPhotos(prev => [...prev, ...result.photos])
					setSuccessMessage(`${result.photos.length} photo${result.photos.length === 1 ? '' : 's'} uploaded successfully`)
					setTimeout(() => setSuccessMessage(''), 3000)
					refreshPreview()
				} else {
					setErrorMessage('Failed to upload photos')
				}
			}
		} catch (error) {
			console.error('Error uploading gallery photo(s):', error)
			setErrorMessage('Failed to upload photo(s)')
		} finally {
			setUploadingGallery(false)
		}
	}

	const handleDeleteGalleryPhoto = async (photoId) => {
		try {
			const result = await deleteMerchantPhoto(photoId)
			if (result === null) return
			if (result) {
				const remainingPhotos = galleryPhotos.filter(p => p.id !== photoId)
				const updatedPhotos = remainingPhotos.map((photo, idx) => ({
					...photo,
					order: idx + 1
				}))
				setGalleryPhotos(updatedPhotos)
				if (updatedPhotos.length > 0) {
					const updates = updatedPhotos.map(p => ({ id: p.id, order: p.order }))
					await updatePhotoOrders(updates)
				}
				setSuccessMessage('Photo deleted successfully')
				setTimeout(() => setSuccessMessage(''), 3000)
				refreshPreview()
			} else {
				setErrorMessage('Failed to delete photo')
			}
		} catch (error) {
			console.error('Error deleting gallery photo:', error)
			setErrorMessage('Failed to delete photo')
		}
	}

	// Replace a gallery photo (delete old, upload new, keep same order)
	const handleReplaceGalleryPhoto = async (photoId, file) => {
		try {
			const photoToReplace = galleryPhotos.find(p => p.id === photoId)
			if (!photoToReplace) return

			setUploadingGallery(true)

			// Delete old photo (skip confirmation dialog)
			const deleteResult = await deleteMerchantPhoto(photoId, true)
			if (!deleteResult) {
				setErrorMessage('Failed to replace photo')
				setUploadingGallery(false)
				return
			}

			// Upload new photo
			const uploadResult = await uploadMerchantGalleryPhoto(merchant.id, file)
			if (uploadResult) {
				// Replace in the array at the same position, preserving order
				const newPhoto = { ...uploadResult.photo, order: photoToReplace.order }
				setGalleryPhotos(prev => prev.map(p => p.id === photoId ? newPhoto : p))

				// Update order in DB
				await updatePhotoOrders([{ id: newPhoto.id, order: newPhoto.order }])

				setSuccessMessage('Photo replaced successfully')
				setTimeout(() => setSuccessMessage(''), 3000)
				refreshPreview()
			} else {
				setErrorMessage('Failed to upload replacement photo')
				loadPhotos()
			}
		} catch (error) {
			console.error('Error replacing gallery photo:', error)
			setErrorMessage('Failed to replace photo')
			loadPhotos()
		} finally {
			setUploadingGallery(false)
		}
	}

	// ============ Drag handlers ============
	const handleDragStart = (e, index) => {
		setDraggedIndex(index)
		e.dataTransfer.effectAllowed = 'move'
		if (e.target) e.dataTransfer.setDragImage(e.target, 0, 0)
	}

	const handleDragOver = (e, index) => {
		e.preventDefault()
		if (draggedIndex === null || draggedIndex === index) {
			setDragOverIndex(null)
			setDropPosition(null)
			return
		}
		const rect = e.currentTarget.getBoundingClientRect()
		// Single column (mobile): use Y-axis, two column (desktop): use X-axis
		let position
		if (isMobile) {
			const midpoint = rect.top + rect.height / 2
			position = e.clientY < midpoint ? 'before' : 'after'
		} else {
			const midpoint = rect.left + rect.width / 2
			position = e.clientX < midpoint ? 'before' : 'after'
		}
		setDragOverIndex(index)
		setDropPosition(position)
	}

	const handleDragLeave = () => { }

	const handleTouchStart = (e, index, element) => {
		e.preventDefault()
		const touch = e.touches[0]
		touchStartDataRef.current = {
			index,
			startY: touch.clientY,
			element,
			elementRect: element.getBoundingClientRect()
		}
		setDraggedIndex(index)

	}

	const handleTouchMove = (e) => {
		e.preventDefault()
		if (touchStartDataRef.current === null || draggedIndex === null) return
		const touch = e.touches[0]
		const touchY = touch.clientY
		if (galleryContainerRef.current) {
			const cards = galleryContainerRef.current.querySelectorAll('[data-photo-card]')
			let foundIndex = null
			let position = null
			cards.forEach((card, idx) => {
				if (idx === draggedIndex) return
				const rect = card.getBoundingClientRect()
				const midpoint = rect.top + rect.height / 2
				if (touchY >= rect.top && touchY <= rect.bottom) {
					foundIndex = idx
					position = touchY < midpoint ? 'before' : 'after'
				}
			})
			if (foundIndex !== null) {
				setDragOverIndex(foundIndex)
				setDropPosition(position)
			} else {
				setDragOverIndex(null)
				setDropPosition(null)
			}
		}
	}

	const handleTouchEnd = async (e) => {
		e.preventDefault()
		touchStartDataRef.current = null
		await handleDragEndLogic()
	}

	const handleDragEndLogic = async () => {
		if (draggedIndex === null || dragOverIndex === null || dropPosition === null) {
			setDraggedIndex(null)
			setDragOverIndex(null)
			setDropPosition(null)
			return
		}
		let targetIndex = dragOverIndex
		if (dropPosition === 'after') targetIndex = dragOverIndex + 1
		if (draggedIndex < targetIndex) targetIndex -= 1
		if (targetIndex === draggedIndex) {
			setDraggedIndex(null)
			setDragOverIndex(null)
			setDropPosition(null)
			return
		}
		const newPhotos = [...galleryPhotos]
		const [draggedPhoto] = newPhotos.splice(draggedIndex, 1)
		newPhotos.splice(targetIndex, 0, draggedPhoto)
		const updatedPhotos = newPhotos.map((photo, idx) => ({
			...photo,
			order: idx + 1
		}))
		setGalleryPhotos(updatedPhotos)
		setDraggedIndex(null)
		setDragOverIndex(null)
		setDropPosition(null)
		try {
			const updates = updatedPhotos.map(photo => ({ id: photo.id, order: photo.order }))
			const success = await updatePhotoOrders(updates)
			if (success) {
				setSuccessMessage('Photo order updated')
				setTimeout(() => setSuccessMessage(''), 2000)
				refreshPreview()
			} else {
				setErrorMessage('Failed to save photo order')
				loadPhotos()
			}
		} catch (error) {
			console.error('Error updating photo order:', error)
			setErrorMessage('Failed to save photo order')
			loadPhotos()
		}
	}

	const handleDragEnd = async () => {
		await handleDragEndLogic()
	}

	// ============ Publish toggle (local only, saved via Save Changes) ============
	const handleTogglePublish = () => {
		setIsPublished(!isPublished)
	}

	// Slug is now just local state, saved via "Save Changes"

	// ============ Save all changes ============
	const handleSaveChanges = async () => {
		// Validate slug if changed
		if (slug !== (merchant?.slug || '')) {
			if (!slug.trim()) {
				setErrorMessage('Slug cannot be empty')
				return
			}
			const slugRegex = /^[a-z0-9-]+$/
			if (!slugRegex.test(slug)) {
				setErrorMessage('Slug can only contain lowercase letters, numbers, and hyphens')
				return
			}
			// Check uniqueness
			try {
				const { data: existing, error: checkError } = await supabase
					.from('merchant')
					.select('id')
					.eq('slug', slug)
					.neq('id', merchant.id)
					.maybeSingle()
				if (checkError) throw checkError
				if (existing) {
					setErrorMessage('This slug is already taken. Please choose a different one.')
					return
				}
			} catch (error) {
				setErrorMessage('Failed to validate slug')
				return
			}
		}

		setSaving(true)
		setErrorMessage('')
		try {
			const socialLinksPayload = {}
			if (bizSocialLinks.instagram?.trim()) socialLinksPayload.instagram = bizSocialLinks.instagram.trim()
			if (bizSocialLinks.google?.trim()) socialLinksPayload.google = bizSocialLinks.google.trim()
			if (bizSocialLinks.yelp?.trim()) socialLinksPayload.yelp = bizSocialLinks.yelp.trim()
			if (bizSocialLinks.facebook?.trim()) socialLinksPayload.facebook = bizSocialLinks.facebook.trim()

			const payload = {
				is_landing_page_active: isPublished,
				slug: slug.trim(),
				address: bizAddress.trim() || null,
				address_2: bizAddress2.trim() || null,
				city: bizCity.trim() || null,
				state: bizState.trim() || null,
				zip: bizZip.trim() || null,
				phone: bizPhone.trim() || null,
				email: bizEmail.trim() || null,
				description: bizDescription.trim() || null,
				website: bizWebsite.trim() || null,
				social_links: socialLinksPayload,
				inherited_hours: bizInheritedHours,
				// While inheriting, force the display copy back to the real store
				// hours — never persist a stale custom set under an inherited flag.
				weekly_hours_display: bizInheritedHours
					? (merchant?.weekly_hours || bizWeeklyHoursDisplay)
					: bizWeeklyHoursDisplay,
			}

			const { data, error } = await supabase
				.from('merchant')
				.update(payload)
				.eq('id', merchant.id)
				.select()
				.single()

			if (error) throw error

			setSuccessMessage('Changes saved successfully')
			setTimeout(() => setSuccessMessage(''), 3000)
			onSaved && onSaved(data)
			refreshPreview()
		} catch (error) {
			console.error('Error saving changes:', error)
			setErrorMessage(error.message || 'Failed to save changes')
		} finally {
			setSaving(false)
		}
	}

	// ============ RENDER ============
	return (
		<Box sx={{ height: '100%', minHeight: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', position: 'relative', padding: '20px', bgcolor: '#ffffff', borderRadius: '12px' }}>
			{/* Page Header */}
			<Box sx={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				mb: 2,
				px: isMobile ? '4px' : '0px',
				py: '0px',
				flexShrink: 0
			}}>
				<Typography
					sx={{
						fontFamily: 'Inter, sans-serif',
						fontWeight: 600,
						fontSize: '28px',
						lineHeight: '1.286em',
						color: '#000000'
					}}
				>
					My Landing Page
				</Typography>
				{!isMobile && (
					<MainButton
						startIcon={<Icon icon="fluent:link-24-regular" width={16} />}
						onClick={handlePreviewPage}
					>
						Preview Page
					</MainButton>
				)}
			</Box>

			{/* Messages */}
			{successMessage && (
				<Alert severity="success" sx={{ mb: 2, mx: '12px', flexShrink: 0 }}>{successMessage}</Alert>
			)}
			{errorMessage && (
				<Alert severity="error" sx={{ mb: 2, mx: '12px', flexShrink: 0 }}>{errorMessage}</Alert>
			)}

			{/* Layout: two-panel on desktop, single column on mobile */}
			<Box sx={{
				display: 'flex',
				flex: 1,
				gap: '10px',
				minHeight: 0
			}}>
				{/* ===== LEFT PANEL / SINGLE COLUMN ===== */}
				<Box
					sx={{
						flex: 1,
						position: 'relative',
						minHeight: 0,
						display: 'flex',
						flexDirection: 'column'
					}}
				>
					<Box
						sx={{
							flex: 1,
							overflowY: 'auto',
							overflowX: 'hidden',
							display: 'flex',
							flexDirection: 'column',
							gap: '10px',
							pr: isMobile ? 0 : '5px',
							minHeight: 0,
							pb: isMobile ? '150px' : (hasUnsavedChanges ? '100px' : '10px')
						}}
					>
						{/* Allow Online Booking Card */}
						<Card>
							<Box sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '8px'
							}}>
								<Box sx={{ flex: 1 }}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '2px' }}>
										<Typography
											sx={{
												fontFamily: 'Inter, sans-serif',
												fontWeight: 600,
												fontSize: '16px',
												lineHeight: '1.5em',
												color: '#2D3748'
											}}
										>
											Allow online booking
										</Typography>
									</Box>
									<Typography
										sx={{
											fontFamily: 'Inter, sans-serif',
											fontWeight: 400,
											fontSize: '14px',
											lineHeight: '1.21em',
											color: '#A0AEC0'
										}}
									>
										Accept bookings through your page
									</Typography>
								</Box>
								{isPublished && (
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: '6px',
											bgcolor: '#CFF7D3',
											border: '1px solid #14AE5C',
											borderRadius: '9999px',
											px: 1.5,
											py: 0.5,
											flexShrink: 0
										}}
									>
										<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#14AE5C' }} />
										<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '13px', color: '#02542D' }}>
											Live
										</Typography>
									</Box>
								)}
								<Switch
									checked={isPublished}
									onChange={handleTogglePublish}
									sx={{
										flexShrink: 0,
										'& .MuiSwitch-switchBase.Mui-checked': { color: '#9F7AEA' },
										'& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#9F7AEA' }
									}}
								/>
							</Box>
						</Card>

						{/* Your Unique Address Card */}
						<Card>
							<Typography
								sx={{
									fontFamily: 'Inter, sans-serif',
									fontWeight: 600,
									fontSize: '16px',
									lineHeight: '1.5em',
									color: '#2D3748',
									mb: '4px'
								}}
							>
								Your unique address
							</Typography>
							<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
								<TextField
									value={slug}
									onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
									placeholder="your-store-name"
									size="small"
									fullWidth
									InputProps={{
										startAdornment: (
											<Typography sx={{ color: '#A0AEC0', mr: 0.5, whiteSpace: 'nowrap', fontSize: '14px' }}>
												{window.location.origin}/store/
											</Typography>
										),
										sx: {
											bgcolor: '#F7F6F3',
											borderRadius: '6px',
											fontSize: '14px',
											'& .MuiOutlinedInput-notchedOutline': {
												borderColor: 'rgba(0, 0, 0, 0.2)'
											}
										}
									}}
								/>
								<IconButton
									onClick={handleCopyLink}
									sx={{
										bgcolor: '#F7F6F3',
										border: '1px solid #CBD5E0',
										borderRadius: '6px',
										p: 1.5,
										flexShrink: 0,
										'&:hover': { bgcolor: '#EDF2F7' }
									}}
								>
									<ContentCopyIcon sx={{ color: copied ? '#14AE5C' : '#9F7AEA', fontSize: 18 }} />
								</IconButton>
							</Box>
						</Card>

						{/* Visual Content - Collapsible (expanded by default) */}
						<CollapsibleSection title="Visual Content" defaultExpanded={true} isMobile={isMobile}>
							{/* Cover Photos Sub-section */}
							<Box>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '18px' }}>
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '15px', color: '#2D3748' }}>
										Cover Photos
									</Typography>
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '15px', color: '#2D3748' }}>
										{galleryPhotos.length}/{MAX_GALLERY_PHOTOS} uploaded
									</Typography>
								</Box>

								<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '13px', color: '#A0AEC0', mb: '18px' }}>
									These photos will be the first thing users see when viewing your landing page. Make sure to make an impression with photos that showcase you and your happy pets!
								</Typography>

								{loadingPhotos ? (
									<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
										<CircularProgress size={24} />
									</Box>
								) : galleryPhotos.length >= MAX_GALLERY_PHOTOS ? (
									<Box
										sx={{
											width: '100%',
											height: '100px',
											bgcolor: '#F7F6F3',
											border: '1px dashed #CBD5E0',
											borderRadius: '12px',
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'center',
											justifyContent: 'center',
											gap: 0.5
										}}
									>
										<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '13px', color: '#A0AEC0' }}>
											Maximum {MAX_GALLERY_PHOTOS} photos reached
										</Typography>
									</Box>
								) : (
									<FileDropZone onFileSelect={handleGalleryFileSelect} label="Upload cover photos" multiple />
								)}

								{uploadingGallery && (
									<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
										<CircularProgress size={24} />
									</Box>
								)}

								<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '13px', color: '#A0AEC0', mt: '18px', whiteSpace: 'pre-line' }}>
									{`Cover Photo Guidelines:\nYour cover preview will show 1-4 photos. Additional photos are available under "see all photos"\nClick on photos to crop and adjust how it appears on your cover\nChoose high-resolution images`}
								</Typography>

								{/* Gallery photos grid with drag reorder */}
								{galleryPhotos.length > 0 && (
									<Box sx={{ mt: '18px' }}>
										<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '12px', color: '#A0AEC0', mb: '8px' }}>
											{isMobile ? 'Touch and drag to reorder' : 'Drag photos to reorder'}
										</Typography>
										<Box
											ref={galleryContainerRef}
											sx={{
												display: 'grid',
												gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
												gap: '12px'
											}}
										>
											{galleryPhotos.map((photo, index) => (
												<Box key={photo.id} data-photo-card>
													<GalleryPhotoCard
														photo={photo}
														index={index}
														onDelete={() => handleDeleteGalleryPhoto(photo.id)}
														onReplace={handleReplaceGalleryPhoto}
														onDragStart={handleDragStart}
														onDragOver={handleDragOver}
														onDragLeave={handleDragLeave}
														onDragEnd={handleDragEnd}
														onTouchStart={handleTouchStart}
														onTouchMove={handleTouchMove}
														onTouchEnd={handleTouchEnd}
														isDragging={draggedIndex === index}
														dropPosition={dragOverIndex === index ? dropPosition : null}
														isMobile={isMobile}
														hideDragHandle={false}
													/>
												</Box>
											))}
										</Box>
									</Box>
								)}
							</Box>
						</CollapsibleSection>

						{/* Business Information - Collapsible (collapsed by default) */}
						<BusinessInfoSection
							bizDescription={bizDescription}
							setBizDescription={setBizDescription}
							bizPhone={bizPhone}
							setBizPhone={setBizPhone}
							bizEmail={bizEmail}
							setBizEmail={setBizEmail}
							bizAddress={bizAddress}
							setBizAddress={setBizAddress}
							bizAddress2={bizAddress2}
							setBizAddress2={setBizAddress2}
							bizCity={bizCity}
							setBizCity={setBizCity}
							bizState={bizState}
							setBizState={setBizState}
							bizZip={bizZip}
							setBizZip={setBizZip}
							bizWebsite={bizWebsite}
							setBizWebsite={setBizWebsite}
							bizSocialLinks={bizSocialLinks}
							setBizSocialLinks={setBizSocialLinks}
							bizInheritedHours={bizInheritedHours}
							setBizInheritedHours={setBizInheritedHours}
							bizWeeklyHoursInherited={bizWeeklyHoursInherited}
							bizWeeklyHoursDisplay={bizWeeklyHoursDisplay}
							setBizWeeklyHoursDisplay={setBizWeeklyHoursDisplay}
							bizClosedDates={bizClosedDates}
							isMobile={isMobile}
						/>

						{/* Reviews - Collapsible (collapsed, placeholder) */}
						<CollapsibleSection title="Reviews" defaultExpanded={false} isMobile={isMobile}>
							<Typography variant="body2" color="text.secondary">
								Reviews section coming soon.
							</Typography>
						</CollapsibleSection>
					</Box>

					{/* Desktop: Save Changes button - centered within left panel */}
					{!isMobile && hasUnsavedChanges && (
						<Box
							sx={{
								position: 'absolute',
								bottom: '32px',
								left: '50%',
								transform: 'translateX(-50%)',
								zIndex: 10
							}}
						>
							<Button
								variant="contained"
								onClick={handleSaveChanges}
								disabled={saving}
								disableElevation
								sx={{
									bgcolor: '#C85AFF',
									color: '#FFFFFF',
									textTransform: 'none',
									fontFamily: 'Inter, sans-serif',
									fontWeight: 600,
									fontSize: '18px',
									lineHeight: '1.21em',
									letterSpacing: '-0.011em',
									borderRadius: '9999px',
									padding: '18px 40px',
									minWidth: 'auto',
									boxShadow: '0px 8px 24px rgba(200, 90, 255, 0.4)',
									'&:hover': { bgcolor: '#B040EE', boxShadow: '0px 8px 28px rgba(200, 90, 255, 0.5)' },
									'&:disabled': { bgcolor: '#CBD5E0', boxShadow: 'none' }
								}}
							>
								{saving ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Save Changes'}
							</Button>
						</Box>
					)}
				</Box>

				{/* ===== RIGHT PANEL - Live Preview (desktop only) ===== */}
				{!isMobile && (
					<Box
						sx={{
							flex: 1,
							display: 'flex',
							flexDirection: 'column',
							overflow: 'hidden'
						}}
					>
						<PreviewPanel
							isMobile={isMobile}
							previewMode={previewMode}
							setPreviewMode={setPreviewMode}
							iframeKey={iframeKey}
							iframeRef={iframeRef}
							mobileContainerRef={mobileContainerRef}
							mobileScale={mobileScale}
							getBookingUrl={getBookingUrl}
							refreshPreview={refreshPreview}
						/>
					</Box>
				)}
			</Box>

			{/* ===== Mobile: Floating bottom bar (Preview + Save Changes) ===== */}
			{isMobile && (
				<Box
					sx={{
						position: 'fixed',
						bottom: 0,
						left: 0,
						right: 0,
						display: 'flex',
						gap: '10px',
						p: '16px 24px',
						pb: '24px',
						bgcolor: 'transparent',
						zIndex: 1200
					}}
				>
					<Button
						variant="outlined"
						onClick={() => setPreviewDrawerOpen(true)}
						disableElevation
						sx={{
							flex: 1,
							textTransform: 'none',
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: '18px',
							lineHeight: '1.21em',
							letterSpacing: '-0.014em',
							borderRadius: '9999px',
							padding: '16px 28px',
							color: '#C85AFF',
							borderColor: '#C85AFF',
							bgcolor: '#FFFFFF',
							'&:hover': { bgcolor: '#FAF0FF', borderColor: '#C85AFF' }
						}}
					>
						Preview
					</Button>
					<Button
						variant="contained"
						onClick={handleSaveChanges}
						disabled={saving || !hasUnsavedChanges}
						disableElevation
						sx={{
							flex: 1,
							textTransform: 'none',
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: '18px',
							lineHeight: '1.21em',
							letterSpacing: '-0.014em',
							borderRadius: '9999px',
							padding: '16px 28px',
							bgcolor: '#C85AFF',
							color: '#FFFFFF',
							'&:hover': { bgcolor: '#B040EE' },
							'&:disabled': { bgcolor: '#CBD5E0' }
						}}
					>
						{saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save Changes'}
					</Button>
				</Box>
			)}

			{/* ===== Mobile: Preview Drawer ===== */}
			{isMobile && (
				<SwipeableDrawer
					anchor="bottom"
					open={previewDrawerOpen}
					onClose={() => setPreviewDrawerOpen(false)}
					onOpen={() => setPreviewDrawerOpen(true)}
					disableSwipeToOpen
					PaperProps={{
						sx: {
							height: '90vh',
							borderRadius: '20px 20px 0 0',
							bgcolor: '#ffffff',
							overflow: 'hidden',
							display: 'flex',
							flexDirection: 'column'
						}
					}}
				>
					{/* Drag handle */}
					<Box sx={{ display: 'flex', justifyContent: 'center', pt: '8px', pb: '16px', flexShrink: 0 }}>
						<Box sx={{ width: '50px', height: '6px', borderRadius: '1000px', bgcolor: '#D9D9D9' }} />
					</Box>

					{/* Preview content */}
					<Box sx={{ flex: 1, overflow: 'hidden', px: isMobile ? '4px' : '16px', pb: '44px', display: 'flex', flexDirection: 'column' }}>
						<PreviewPanel
							isMobile={isMobile}
							previewMode={previewMode}
							setPreviewMode={setPreviewMode}
							iframeKey={iframeKey}
							iframeRef={iframeRef}
							mobileContainerRef={mobileContainerRef}
							mobileScale={mobileScale}
							getBookingUrl={getBookingUrl}
							refreshPreview={refreshPreview}
						/>
					</Box>
				</SwipeableDrawer>
			)}

		</Box>
	)
}
